import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Banknote, Truck, ChevronLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/lib/seo";
import { useContent } from "@/lib/content";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/format";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

export default function Produit() {
  const { slug } = useParams();
  const { add, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState("");
  const { t } = useContent();
  const [imgIdx, setImgIdx] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setProduct(null);
    setQty(1);
    setImgIdx(0);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        if (r.data.category) {
          api
            .get("/products", { params: { category: r.data.category, limit: 8 } })
            .then((res) => setRelated(res.data.filter((p) => p.id !== r.data.id).slice(0, 3)))
            .catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-5 px-6 text-center pt-24">
        <p className="font-serif text-4xl text-teal-dark">Produit introuvable</p>
        <p className="text-ink/70">Ce produit n'existe plus ou n'est plus disponible.</p>
        <Link data-testid="product-notfound-back" to="/boutique" className="rounded-full bg-teal px-8 py-3 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          Retour à la boutique
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="min-h-[70vh] pt-36 max-w-7xl mx-auto px-6"><div className="h-96 rounded-3xl bg-cream-light animate-pulse" /></div>;
  }

  const perVariant = product.variants?.length && product.variant_stock && Object.keys(product.variant_stock).length > 0;
  const stock = perVariant && variant ? Number(product.variant_stock[variant]) || 0 : Number(product.stock) || 0;
  const soldOut = stock <= 0;
  const low = !soldOut && stock <= 3;
  const hasVariants = (product?.variants || []).length > 0;
  const needVariant = hasVariants && !variant;

  return (
    <div data-testid="product-detail-page">
      <SEO title={product.name} description={product.description?.slice(0, 155)} image={product.images?.[0]} type="product" />
      <section className="pt-32 md:pt-40 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link data-testid="product-back-link" to="/boutique" className="inline-flex items-center gap-1.5 text-sm text-mauve hover:text-teal transition-colors mb-8">
            <ChevronLeft size={16} /> Retour à la boutique
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <motion.div
                key={imgIdx}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="aspect-square overflow-hidden rounded-[2rem] border-4 border-cream shadow-[0_24px_70px_rgba(16,114,105,0.15)] bg-cream-light"
              >
                <img data-testid="product-main-image" src={product.images?.[imgIdx] || product.images?.[0]} alt={product.name} className="h-full w-full object-cover" />
              </motion.div>
              {product.images?.length > 1 && (
                <div className="mt-4 flex gap-3 justify-center">
                  {product.images.map((img, i) => (
                    <button key={img} data-testid={`product-thumb-${i}`} onClick={() => setImgIdx(i)} className={`h-16 w-14 overflow-hidden rounded-xl border-2 transition ${i === imgIdx ? "border-teal" : "border-transparent opacity-60 hover:opacity-100"}`}>
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="eyebrow mb-3">{product.category ? product.category.replace(/-/g, " ") : "Boutique"}</p>
              <h1 data-testid="product-name" className="font-serif text-3xl md:text-5xl text-teal-dark leading-tight">{product.name}</h1>
              <div className="mt-4 flex items-center gap-3">
                <span data-testid="product-detail-stock" className={`rounded-full px-4 py-1.5 text-xs font-mono uppercase tracking-wider ${soldOut ? "bg-[#b04a4a]/10 text-[#b04a4a]" : low ? "bg-tan/40 text-[#7c4a1e]" : "bg-mint/25 text-teal"}`}>
                  {soldOut ? "Épuisé" : low ? `Plus que ${stock} en stock` : "Disponible"}
                </span>
              </div>
              <p data-testid="product-detail-price" className="mt-6 text-3xl font-semibold text-teal">
                {product.old_price && <span className="mr-3 text-lg text-mauve line-through">{formatPrice(product.old_price)}</span>}
                {formatPrice(product.price)}
              </p>
              <p data-testid="product-description" className="mt-6 text-ink/80 leading-relaxed whitespace-pre-line">{product.description}</p>

              {hasVariants && (
                <div className="mt-8" data-testid="product-variants">
                  <p className="text-sm font-medium text-ink">
                    {product.variant_label || "Couleur"} <span className="text-mauve font-normal">— {variant || "à choisir"}</span>
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {product.variants.map((v) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`product-variant-${v.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setVariant(v)}
                        disabled={perVariant && !(Number(product.variant_stock[v]) > 0)}
                        aria-pressed={variant === v}
                        className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors disabled:opacity-40 disabled:line-through disabled:cursor-not-allowed ${variant === v ? "border-teal bg-teal text-cream" : "border-line bg-white text-teal-dark hover:border-teal"}`}
                      >
                        {v}{perVariant && Number(product.variant_stock[v]) > 0 && Number(product.variant_stock[v]) <= 3 ? ` (${product.variant_stock[v]})` : ""}
                      </button>
                    ))}
                  </div>
                  {needVariant && <p data-testid="product-variant-hint" className="mt-2 text-xs text-[#b04a4a]">Choisissez une option pour ajouter au panier.</p>}
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <div className="flex items-center rounded-full border border-line">
                  <button data-testid="product-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-ink hover:text-teal" aria-label="Diminuer la quantité"><Minus size={16} /></button>
                  <span data-testid="product-qty" className="w-10 text-center font-medium">{qty}</span>
                  <button data-testid="product-qty-plus" onClick={() => setQty((q) => Math.min(q + 1, stock || 1))} disabled={soldOut || qty >= stock} className="p-3 text-ink hover:text-teal disabled:opacity-30" aria-label="Augmenter la quantité"><Plus size={16} /></button>
                </div>
                <button
                  data-testid="product-add-to-cart"
                  onClick={() => add({ ...product, stock }, qty, variant)}
                  disabled={soldOut || needVariant}
                  className="inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 font-medium text-cream hover:bg-teal-dark transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ShoppingBag size={17} />
                  {soldOut ? t("product.soldout") : t("product.add")}
                </button>
                {!soldOut && (
                  <button
                    data-testid="product-buy-now"
                    onClick={() => { if (needVariant) return; add({ ...product, stock }, qty, variant); setOpen(false); }}
                    className="link-underline text-sm font-medium text-teal"
                  >
                    {t("product.buy")}
                  </button>
                )}
              </div>

              <div className="mt-10 space-y-4 border-t border-line pt-8">
                <div className="flex gap-4">
                  <Truck size={20} className="text-teal shrink-0 mt-0.5" />
                  <p className="text-sm text-ink/80"><strong className="text-teal-dark">{t("product.info1.title")}</strong> — {t("product.info1.text")}</p>
                </div>
                <div className="flex gap-4">
                  <Banknote size={20} className="text-teal shrink-0 mt-0.5" />
                  <p className="text-sm text-ink/80"><strong className="text-teal-dark">{t("product.info2.title")}</strong> — {t("product.info2.text")}</p>
                </div>
                <div className="flex gap-4">
                  <ShieldCheck size={20} className="text-teal shrink-0 mt-0.5" />
                  <p className="text-sm text-ink/80"><strong className="text-teal-dark">{t("product.info3.title")}</strong> — {t("product.info3.text")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-cream-light">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Reveal>
              <h2 className="font-serif text-2xl md:text-4xl text-teal-dark mb-10">Vous aimerez aussi</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
